/**
 * PLAT-364 (Shell 2a): the shell navigation as data, for the Workspace AppShell
 * wrapper (components/AppShell.tsx) to hand Foundation's AppShell as `NavNode`s.
 * The admin tree is the old admin sidebar's, unchanged: labels, hrefs, order,
 * icons and `ownerOnly` filtering, and its search index with the same keywords.
 * PLAT-365 (Shell 3) adds the user tree: TopNav's user-mode links, unchanged.
 *
 * Labels are user-facing copy and URLs are implementation (Ean, 2026-06-10):
 * "People" stays at /admin/people. Don't align them.
 *
 * Every admin href stays inside /admin/* (scripts/check-policies.sh check 5);
 * shellNav.test.tsx and shellNavigation.test.tsx pin that.
 */

import type { NavNode } from '@olytic/foundation';
import type { ReactNode } from 'react';
import type { Role } from '../lib/types.ts';
import type { ObjectKind } from './ObjectIcon.tsx';

export interface ShellNavEntry {
  label: string;
  href: string;
  /** The ObjectIcon kind; Foundation shows it on top-level rows only. */
  iconKind: ObjectKind;
  /** Active only on this exact path, as react-router's NavLink `end`. */
  end?: boolean;
  ownerOnly?: boolean;
  /** Shown only while the server reports artifacts enabled (PLAT-213, /api/me artifacts_enabled). */
  artifactsOnly?: boolean;
  /** Shows the pending invitations plus open requests count. */
  counted?: boolean;
}

export interface ShellNavGroup {
  /** The group's key in Foundation's AppShell. */
  group: string;
  label: string;
  iconKind: ObjectKind;
  children: ShellNavEntry[];
}

export type ShellNavNode = ShellNavEntry | ShellNavGroup;

const isGroup = (n: ShellNavNode): n is ShellNavGroup => 'group' in n;

/** The admin sidebar's 6-item IA (Push 5-post-smoke-32), in order. No entry is ownerOnly today. */
export const ADMIN_NAV: ShellNavNode[] = [
  { label: 'Home', href: '/admin', iconKind: 'home', end: true },
  { label: 'Workspace', href: '/admin/workspace', iconKind: 'workspace', end: true },
  { label: 'People', href: '/admin/people', iconKind: 'people', counted: true },
  {
    group: 'capabilities',
    label: 'Capabilities',
    iconKind: 'capabilities',
    // D-167: Models → Connectors → Prompts → Library → Market
    children: [
      { label: 'Models', href: '/admin/models', iconKind: 'models' },
      { label: 'Connectors', href: '/admin/connectors', iconKind: 'connectors' },
      { label: 'Prompts', href: '/admin/prompts', iconKind: 'prompts' },
      { label: 'Library', href: '/admin/library', iconKind: 'library' },
      { label: 'Market', href: '/admin/market', iconKind: 'catalog' },
    ],
  },
  {
    group: 'permissions',
    label: 'Permissions',
    iconKind: 'permission-sets',
    children: [
      {
        label: 'Permission sets',
        href: '/admin/permission-sets',
        iconKind: 'permission-set-shield',
      },
      { label: 'Assignments', href: '/admin/assignments', iconKind: 'assignment-link' },
    ],
  },
  {
    group: 'analytics',
    label: 'Analytics',
    iconKind: 'analytics',
    children: [
      { label: 'Overview', href: '/admin/analytics', iconKind: 'dashboard', end: true },
      { label: 'Reports', href: '/admin/analytics/reports', iconKind: 'token-debit' },
      { label: 'Activity', href: '/admin/analytics/activity', iconKind: 'pulse' },
    ],
  },
];

/**
 * TopNav's user-mode links, in order (IA per OC 2026-06-09, D-167 inside
 * Capabilities). Activity stays out of the user lane (Ean 2026-06-09, D-153).
 */
export const USER_NAV: ShellNavNode[] = [
  { label: 'Chat', href: '/chat', iconKind: 'chat' },
  { label: 'Home', href: '/home', iconKind: 'home', end: true },
  { label: 'Artifacts', href: '/artifacts', iconKind: 'document', artifactsOnly: true },
  {
    group: 'capabilities',
    label: 'Capabilities',
    iconKind: 'capabilities',
    children: [
      { label: 'Models', href: '/models', iconKind: 'models' },
      { label: 'Connectors', href: '/connectors', iconKind: 'connectors' },
      { label: 'Prompts', href: '/prompts', iconKind: 'prompts' },
      { label: 'Library', href: '/library', iconKind: 'library' },
    ],
  },
  { label: 'Market', href: '/market', iconKind: 'catalog' },
];

export interface ShellNavSearchEntry {
  label: string;
  href: string;
  keywords: string[];
  parent?: string;
  ownerOnly?: boolean;
}

/**
 * The admin sidebar's search (Push #17): wayfinding over the nav, its page
 * tabs and aliases. Data search is GlobalSearch.
 */
// biome-ignore format: one entry per line reads as the table it is.
export const ADMIN_NAV_SEARCH: ShellNavSearchEntry[] = [
  { label: 'Home', href: '/admin', keywords: ['home', 'overview', 'dashboard'] },
  { label: 'General', href: '/admin/workspace', keywords: ['workspace', 'general', 'overview', 'settings', 'preferences', 'name', 'timezone', 'default model', 'memory'], parent: 'Workspace' },
  { label: 'Usage', href: '/admin/workspace/usage', keywords: ['usage', 'team pot', 'credits', 'balance', 'monthly cap', 'monthly limit', 'auto-reload', 'auto reload', 'buy credits', 'per-user', 'spend cap', 'wallet', 'personal cap', 'tags'], parent: 'Workspace' },
  { label: 'Billing', href: '/admin/workspace/billing', keywords: ['billing', 'invoices', 'payment method', 'card', 'saved card', 'credit card', 'receipts', 'stripe'], parent: 'Workspace' },
  { label: 'Notifications', href: '/admin/workspace/notifications', keywords: ['notifications', 'recipients', 'alerts', 'email'], parent: 'Workspace' },
  { label: 'People', href: '/admin/people', keywords: ['people', 'users', 'team', 'members', 'invitations', 'approvals', 'deactivated', 'inactive'] },
  { label: 'Users', href: '/admin/people', keywords: ['users', 'everyone', 'roster', 'all users', 'members', 'team'], parent: 'People' },
  { label: 'Invitations', href: '/admin/people?tab=invitations', keywords: ['invitations', 'invites', 'invite'], parent: 'People' },
  // "Requests" matches the on-page tab; ?tab=approvals still works for old bookmarks.
  { label: 'Requests', href: '/admin/people?tab=requests', keywords: ['requests', 'approvals', 'awaiting approval', 'credit approvals', 'pending approvals', 'access requests', 'help', 'support'], parent: 'People' },
  { label: 'Inactive users', href: '/admin/people?status=inactive', keywords: ['deactivated', 'inactive', 'removed', 'frozen'], parent: 'People' },
  { label: 'Models', href: '/admin/models', keywords: ['models', 'llm', 'ai'], parent: 'Capabilities' },
  { label: 'Connectors', href: '/admin/connectors', keywords: ['connectors', 'apps', 'integrations'], parent: 'Capabilities' },
  { label: 'Prompts', href: '/admin/prompts', keywords: ['prompts', 'knowledge', 'templates'], parent: 'Capabilities' },
  { label: 'Library', href: '/admin/library', keywords: ['library', 'documents', 'knowledge base', 'files'], parent: 'Capabilities' },
  { label: 'Market', href: '/admin/market', keywords: ['market', 'marketplace', 'catalog', 'browse'], parent: 'Capabilities' },
  { label: 'Permission sets', href: '/admin/permission-sets', keywords: ['permission sets', 'roles', 'permissions', 'bundles'], parent: 'Permissions' },
  { label: 'Assignments', href: '/admin/assignments', keywords: ['assignments', 'grants', 'permissions'], parent: 'Permissions' },
  // Legacy 'spend' and 'insights' route to their new homes for muscle memory.
  { label: 'Overview', href: '/admin/analytics', keywords: ['analytics', 'overview', 'summary', 'dashboard', 'insights', 'callouts', 'optimization', 'top users', 'top models'], parent: 'Analytics' },
  { label: 'Reports', href: '/admin/analytics/reports', keywords: ['reports', 'spend', 'cost', 'spending', 'credits', 'by user', 'by model', 'by capability', 'by connector', 'by prompt', 'by tag', 'by day', 'pivot', 'drilldown', 'csv', 'export'], parent: 'Analytics' },
  { label: 'Activity', href: '/admin/analytics/activity', keywords: ['activity', 'runs', 'dispatches', 'ai calls', 'audit', 'admin actions', 'event log', 'compliance'], parent: 'Analytics' },
];

const visibleTo =
  (role: Role, artifacts = false) =>
  (e: { ownerOnly?: boolean; artifactsOnly?: boolean }) =>
    !(e.ownerOnly === true && role !== 'owner') && !(e.artifactsOnly === true && !artifacts);

/**
 * The tree as `role` sees it: ownerOnly entries for the owner only,
 * artifactsOnly entries only when `artifacts` is on, empty groups dropped.
 */
export function navFor(tree: ShellNavNode[], role: Role, artifacts = false): ShellNavNode[] {
  const visible = visibleTo(role, artifacts);
  return tree.flatMap((node): ShellNavNode[] => {
    if (!isGroup(node)) return visible(node) ? [node] : [];
    const children = node.children.filter(visible);
    return children.length > 0 ? [{ ...node, children }] : [];
  });
}

/** Whether `pathname` is at an entry, as react-router's NavLink matches it. */
export function isAt(entry: ShellNavEntry, pathname: string): boolean {
  if (pathname === entry.href) return true;
  return entry.end !== true && pathname.startsWith(`${entry.href}/`);
}

/** The href (the AppShell key) of the entry `pathname` is at, if any. */
export function activeNavKey(tree: ShellNavNode[], pathname: string): string | undefined {
  const leaves = tree.flatMap((n) => (isGroup(n) ? n.children : [n]));
  return leaves.find((e) => isAt(e, pathname))?.href;
}

/** The groups holding the entry `pathname` is at: open on first render, as the old sidebar did. */
export function openNavGroups(tree: ShellNavNode[], pathname: string): string[] {
  return tree.flatMap((n) =>
    isGroup(n) && n.children.some((c) => isAt(c, pathname)) ? [n.group] : [],
  );
}

/** Search hits for `query`: label or keyword contains it, at most 12, as the old sidebar listed them. */
export function searchNav(
  index: ShellNavSearchEntry[],
  role: Role,
  query: string,
): ShellNavSearchEntry[] {
  const term = query.trim().toLowerCase();
  if (term === '') return [];
  return index
    .filter(visibleTo(role))
    .filter(
      (e) =>
        e.label.toLowerCase().includes(term) ||
        e.keywords.some((k) => k.toLowerCase().includes(term)),
    )
    .slice(0, 12);
}

/** Foundation `NavNode`s; `icon` draws an ObjectIcon kind, `count` is the People count. */
export function toNavNodes(
  tree: ShellNavNode[],
  icon: (kind: ObjectKind) => ReactNode,
  count?: number,
): NavNode[] {
  const leaf = (e: ShellNavEntry): NavNode => ({
    key: e.href,
    label: e.label,
    href: e.href,
    icon: icon(e.iconKind),
    ...(e.counted === true && count !== undefined && count > 0 ? { count } : {}),
  });
  return tree.map((n) =>
    isGroup(n)
      ? { key: n.group, label: n.label, icon: icon(n.iconKind), children: n.children.map(leaf) }
      : leaf(n),
  );
}

/** Search hits as nav rows, labelled "Parent → Label" as the old sidebar did. */
export function hitNodes(hits: ShellNavSearchEntry[]): NavNode[] {
  return hits.map((h) => ({
    key: `search:${h.href}`,
    label: h.parent !== undefined ? `${h.parent} → ${h.label}` : h.label,
    href: h.href,
  }));
}
